import { throttle } from "es-toolkit";
import { useLayoutEffect, useRef } from "react";

function getCenter(el: HTMLElement | undefined | null) {
  if (!el) {
    return 0;
  }
  return (
    (el.getBoundingClientRect().left + el.getBoundingClientRect().right) / 2
  );
}

function linearInterpolation(
  ui: number,
  [uiMin, uiMax]: [number, number],
  [domainMin, domainMax]: [number, number]
): number {
  return ((ui - uiMin) * (domainMax - domainMin)) / (uiMax - uiMin) + domainMin;
}

export function useRangeHandles<
  HandleRef extends HTMLElement = HTMLDivElement,
  AreaRef extends HTMLElement = HTMLDivElement
>({
  setMin,
  setMax,
  scale,
}: {
  setMin: (min: number) => void;
  setMax: (max: number) => void;
  scale: [number, number];
}) {
  const leftX = useRef(0);
  const rightX = useRef(0);

  const minCurrentX = useRef(0);
  const minRef = useRef<HandleRef | null>(null);
  const minInitialX = useRef(0);
  const minIsDragging = useRef(false);

  const maxCurrentX = useRef(0);
  const maxRef = useRef<HandleRef | null>(null);
  const maxInitialX = useRef(0);
  const maxIsDragging = useRef(false);

  const areaRef = useRef<AreaRef | null>(null);

  useLayoutEffect(() => {
    leftX.current = getCenter(minRef.current);
    rightX.current = getCenter(maxRef.current);

    function updateState() {
      const uiRange = rightX.current - leftX.current;
      const min = linearInterpolation(minCurrentX.current, [0, uiRange], scale);
      const max = linearInterpolation(
        uiRange + maxCurrentX.current,
        [0, uiRange],
        scale
      );
      setMin(min);
      setMax(max);
    }

    const throttledUpdateState = throttle(updateState, 300);

    function updateArea() {
      if (!areaRef.current) {
        return;
      }
      const left = getCenter(minRef.current);
      const right = getCenter(maxRef.current);
      areaRef.current.style.setProperty("width", `${right - left}px`);
      areaRef.current.style.setProperty(
        "transform",
        `translateX(${minCurrentX.current}px)`
      );
    }
    function onMinMouseDown(e: MouseEvent) {
      minInitialX.current = e.clientX;
      minIsDragging.current = true;
    }

    function onMinMouseUp(e: MouseEvent) {
      minIsDragging.current = false;
      updateArea();
      updateState();
    }

    function onMinMouseMove(e: MouseEvent) {
      if (!minIsDragging.current) {
        return;
      }
      const dx = e.clientX - minInitialX.current;
      const newX = minCurrentX.current + dx;
      if (newX < 0) {
        return;
      }
      minCurrentX.current = newX;
      minInitialX.current = e.clientX;
      minRef.current?.style.setProperty(
        "transform",
        `translateX(${minCurrentX.current}px)`
      );
      updateArea();
      throttledUpdateState();
    }

    function onMaxMouseDown(e: MouseEvent) {
      maxInitialX.current = e.clientX;
      maxIsDragging.current = true;
    }

    function onMaxMouseUp(e: MouseEvent) {
      maxIsDragging.current = false;
    }

    function onMaxMouseMove(e: MouseEvent) {
      if (!maxIsDragging.current) {
        return;
      }
      const dx = e.clientX - maxInitialX.current;
      const newX = maxCurrentX.current + dx;
      if (newX > 0) {
        return;
      }
      maxCurrentX.current += dx;
      maxInitialX.current = e.clientX;
      maxRef.current?.style.setProperty(
        "transform",
        `translateX(${maxCurrentX.current}px)`
      );
      updateArea();
      throttledUpdateState();
    }

    document.addEventListener("mousemove", onMinMouseMove);
    minRef.current?.addEventListener("mousedown", onMinMouseDown);
    document.addEventListener("mouseup", onMinMouseUp);

    document.addEventListener("mousemove", onMaxMouseMove);
    maxRef.current?.addEventListener("mousedown", onMaxMouseDown);
    document.addEventListener("mouseup", onMaxMouseUp);

    updateState();
    updateArea();

    return () => {
      document.removeEventListener("mousemove", onMinMouseMove);
      minRef.current?.removeEventListener("mousedown", onMinMouseDown);
      document.removeEventListener("mouseup", onMinMouseUp);

      document.removeEventListener("mousemove", onMaxMouseMove);
      maxRef.current?.removeEventListener("mousedown", onMaxMouseDown);
      document.removeEventListener("mouseup", onMaxMouseUp);
    };
  }, []);

  return { minRef, maxRef, areaRef };
}
